import { useEffect } from "react";
import {
	SalesPartFormContent,
	SalesPartFormValues,
} from "src/components/views/erp/sales/masterData/salesParts/SalesPartFormContent.tsx";
import { useAavoForm } from "src/components/common/forms/useAavoForm/useAavoForm.ts";
import { AavoFormLayout } from "src/components/common/forms/AavoFormLayout.tsx";
import { PartCreationWizardApi_FormInitData } from "src/api/generated/erp/parts/catalogPart/api/partCreationWizardApi.ts";
import { logError } from "src/errorHandling/errorLogging.ts";
import { PartCreationWizardState } from "src/components/views/erp/parts/catalogPart/partCreationWizard/types.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { getSalesPartFormDefaultValues } from "src/components/views/erp/sales/masterData/salesParts/SalesPartForm.utils.ts";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";
import i18n from "i18next";

interface SalesPartFormStepProps {
	sitePart: PartCreationWizardState["sitePart"];
	catalogPart: PartCreationWizardState["catalogPart"];
	salesPart: PartCreationWizardState["salesPart"];
	createSalesPart: boolean;
	onUpdateCreateSalesPart: (createSalesPart: boolean) => void;
	onUpdateSalesPart: (updates: PartCreationWizardState["salesPart"]) => void;
	initData: PartCreationWizardApi_FormInitData;
	onValidationChange?: (isValid: boolean) => void;
}

export const SalesPartFormStep = ({
	sitePart,
	catalogPart,
	createSalesPart,
	onUpdateCreateSalesPart,
	salesPart: currentSalesPart,
	onUpdateSalesPart,
	initData,
	onValidationChange,
}: SalesPartFormStepProps) => {
	const { defaultSiteId } = useGlobalInitData();

	const defaultValues = {
		...getSalesPartFormDefaultValues({
			part: {
				partNo: catalogPart.manuallyInsertedPartNumber,
				partDescription_1: catalogPart.description_1,
				partDescription_2: catalogPart.description_2,
				partUnitId: catalogPart.partUnitId,
				siteId: sitePart.siteId,
				acquisitionMethod: sitePart.acquisitionMethod,
			},
			defaultSiteId: sitePart.siteId ?? defaultSiteId,
			vatCodeOptions: initData.vatCodeOptions,
			accountingDimensions: initData.salesPartAccountingDimensions,
		}),
		...currentSalesPart,
	};

	const useAavoFormReturn = useAavoForm<SalesPartFormValues, void>({
		defaultValues: defaultValues,
		onSubmit: () => {},
	});
	const { watch, formState, trigger } = useAavoFormReturn;

	// Update values on first render
	useEffect(() => {
		onUpdateSalesPart(defaultValues);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const subscription = watch((newValues) => {
			onUpdateSalesPart(newValues);
		});
		return () => subscription.unsubscribe();
	}, [watch, onUpdateSalesPart]);

	useEffect(() => {
		if (!formState.isValid) console.debug(formState.errors);
		onValidationChange?.(createSalesPart ? formState.isValid : true);
	}, [formState.isValid, formState.errors, onValidationChange, createSalesPart]);

	useEffect(() => {
		trigger().catch(logError);
	}, [trigger]);

	return (
		<AavoFormLayout columns={3}>
			<AavoCheckbox
				label={i18n.t("create_sales_part")}
				checked={createSalesPart}
				onChange={(checked) => onUpdateCreateSalesPart(checked)}
				sx={{ gridColumn: "1/-1" }}
			/>
			{createSalesPart && (
				<SalesPartFormContent
					{...useAavoFormReturn}
					salesPartId={null}
					salesPartNoRequired={false}
					part={{
						...sitePart,
						isConfigurable: catalogPart.isConfigurable,
					}}
					initData={initData}
				/>
			)}
		</AavoFormLayout>
	);
};
